import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PersonaUpdate } from 'src/app/_models/persona-update';
import { environment } from 'src/environments/environment';
import { shareReplay, concatMap, map, tap, toArray, filter, catchError, mergeMap } from 'rxjs/operators';
import { from, Observable, combineLatest, BehaviorSubject, throwError, Subject } from 'rxjs';
import { CustomAlertService } from 'src/app/_components/custom-alert/custom-alert.service';
import * as moment from 'moment';
import { GlobalService } from 'src/app/_services/global.service';

const API_URL = environment.apiUrl;
@Injectable({providedIn: 'root'})
export class ActualizacionDatosService {

  idHijosNavegacion = [];
  idHijos$ = this.http.get<Array<number>>(`${API_URL}/api/Personas/hijos`).pipe(
    map(data => {
      if (this.idHijosNavegacion.length > 0) {
        return this.idHijosNavegacion;
      } else {
        return data;
      }
    }),
    shareReplay(1)
  );

  updateDataHijos$ = this.idHijos$.pipe(
    map(data => {
      if (data.length === 0) {
        return throwError(() => new Error('No hay hijos asosciados a usted. Por favor comuniquese con soporte.'));
      } else {
        return data;
      }
    }),
    concatMap((idArray) => {
      return from(idArray).pipe(
        concatMap((id) => {
          return this.http.get<PersonaUpdate>(`${API_URL}/api/Personas/update/${id}`);
        }),
      );
    }),
    catchError((err) => {
      this.customAlert.setCustomMessage({
        tipo: 'error',
        texto: `No se ha podido cargar la información. <br> Código de error: ${err.statusText}`
      });
      throw err;
    }),
    shareReplay(10),
  );

  ciudades$ = this.globalService.ciudades$;

  rhs$ = this.globalService.rhs$;

  documentos$ = this.globalService.documentos$;

  parentescos = from(['Madre', 'Padre']);

  updateDataPadres$ = this.updateDataHijos$.pipe(
    mergeMap((hijo) => {
      // Por cada tipo de parentesco busca en el servidor un registro y retorna un objeto con el hijo
      // y sus padres correspondientes en un arreglo.
      return this.parentescos.pipe(
        mergeMap((parentesco) => {
          return this.http.get<Array<PersonaUpdate>>(`${API_URL}/api/Personas/familia/${hijo.id}/${parentesco}`).pipe(
            map((data) => {
              data.forEach(item => {
                if (item.sexo === 0) {
                  item.sexo = parentesco === 'Madre' ? 1 : 2;
                }
              });
              return data;
            }),
            mergeMap((data) => {
              return from(data).pipe(
                filter(padre => padre.isVive)
              );
            })
          );
        }),
        toArray(),
        map(data => {
          return {
            hijo: hijo,
            padres: data
          };
        })
      );
    }),
    catchError((err) => {
      this.customAlert.setCustomMessage({
        tipo: 'error',
        texto: `No se ha podido cargar la información. <br> Código de error: ${err.statusText}`
      });
      throw err;
    }),
    toArray(),
    shareReplay(1)
  );

  private responseStreamSubject = new BehaviorSubject(new PersonaUpdate({}));
  responseStream$ = this.responseStreamSubject.asObservable();

  // Por cada respuesta de guardado valida del servidor se actualiza el observable de
  // los datos con esa información.
  updateData$ = combineLatest([this.updateDataPadres$, this.responseStream$]).pipe(
    map(([data, response]) => {
      data.forEach(item => {
        if (item.hijo.id === response.id) {
          item.hijo = this.updateObject(item.hijo, response);
        }
        item.padres.forEach(padre => {
          if (padre.id === response.id) {
            padre = this.updateObject(padre, response);
          }
        });
      });
      return data;
    })
  );

  private cambioDeEstadoCompletitudSubject = new Subject<{ id: number, personaId: number, isCompleted: boolean }>();

  completitud$ = this.cambioDeEstadoCompletitudSubject.asObservable();

  constructor(
    private http: HttpClient,
    private customAlert: CustomAlertService,
    private globalService: GlobalService,
    // private matriculaService: MatriculaService
  ) {
  }

  guardar(form: any, url: string): Observable<any> {
    return this.http.post<PersonaUpdate>(`${API_URL}/api/Personas/update/${url}`, form).pipe(
      catchError((err) => {
        this.customAlert.setCustomMessage({
          tipo: 'error',
          texto: `Error al guardar, por favor intentar nuevamente.
             Si el error persiste comunicarse con soporte <br> Código de error: ${err.statusText}`
        });
        throw err;
      }),
      tap(data => {
        // this.cambioDeEstadoCompletitudSubject.next({ id: -1, personaId: data.id, isCompleted: data?.isActualizado ?? false });

        if (data.hasOwnProperty('isActualizado')) {
          if (data.isActualizado === true) {
            this.cambioDeEstadoCompletitudSubject.next({ id: -1, personaId: data.id, isCompleted: data.isActualizado });
          }
        }
        this.responseStreamSubject.next(data);
      }),
    );
  }

  updateObject(old: any, newO: any) {
    Object.keys(newO).forEach(property => {
      if (old.hasOwnProperty(property)) {
        if (newO[property]) {
          old[property] = newO[property];
        }
      }
    });
    return old;
  }

  labelPadres(sexo: number): string {
    return `Datos ${sexo === 2 ? 'del padre' : 'de la madre'}`;
  }

  isActual(persona: PersonaUpdate) {
    const momentActualiza = moment(persona.fechaActualiza, 'DD/MM/YYYY');
    return momentActualiza.diff(moment.now(), 'months') < -3 ? false : true;
  }

  getHijo(id: number): Observable<PersonaUpdate> {
    return this.http.get<PersonaUpdate>(`${API_URL}/api/Personas/update/${id}`);
  }

  getPadres(id: number): Observable<Array<PersonaUpdate>> {
    return this.parentescos.pipe(
      mergeMap((parentesco) => {
        return this.http.get<Array<PersonaUpdate>>(`${API_URL}/api/Personas/familia/${id}/${parentesco}`).pipe(
          map((data) => {
            data.forEach(item => {
              if (item.sexo === 0) {
                item.sexo = parentesco === 'Madre' ? 1 : 2;
              }
            });
            return data;
          }),
          mergeMap((data) => {
            return from(data).pipe(
              filter(padre => padre.isVive)
            );
          })
        );
      }),
      toArray()
    );
  }
}
